import { useEffect, useRef, useState, type FormEvent, type KeyboardEvent } from 'react';
import { POS_LABELS, type Inflection, type Lookup, type Reading, type Sense } from '@realmery/realm-core';
import { isPressed, keyLabel, keysOf } from '../shortcuts.ts';
import { closeThesaurus, thesaurus, useThesaurusPanel, useThesaurusStatus } from './client.ts';

/**
 * The Thesaurus panel (docs/PARITY-STEP-6-PLAN.md section 5), as the desktop's
 * `ThesaurusPanel` (Shift+F7): every meaning of a word, grouped by how the word was read,
 * each with its definition, an example, and its synonyms, similar, more specific and more
 * general words, and opposites. Click a word to pick it as the replacement, already in
 * the right form ("strode" for "walked"); double-click to look it up and browse on; Back
 * and Forward retrace the way. Replace puts the pick in place of the word it was opened on.
 */

const FORM_NAMES: Partial<Record<Inflection, string>> = {
  PLURAL: 'plural',
  PAST: 'past tense',
  PAST_PARTICIPLE: 'past participle',
  PRESENT_PARTICIPLE: '-ing form',
  THIRD_PERSON: 'present tense',
  COMPARATIVE: 'comparative',
  SUPERLATIVE: 'superlative',
};

const sameReading = (a: Reading | null, b: Reading) => a !== null && a.lemma === b.lemma && a.partOfSpeech === b.partOfSpeech && a.inflection === b.inflection;
const matches = (event: KeyboardEvent, actionId: string) => isPressed(event.nativeEvent, actionId);

export function ThesaurusPanel() {
  const { target: opened, opened: openedCount } = useThesaurusPanel();
  const status = useThesaurusStatus();
  const [target, setTarget] = useState(opened);
  const [search, setSearch] = useState('');
  const [lookup, setLookup] = useState<Lookup | null>(null);
  const [isTarget, setIsTarget] = useState(false);
  const [history, setHistory] = useState<{ words: string[]; at: number }>({ words: [], at: -1 });
  /** How the target word was read, so a word picked while browsing still takes its form. */
  const targetReading = useRef<Reading | null>(null);
  const [picked, setPicked] = useState<{ key: string; word: string } | null>(null);
  const [replacement, setReplacement] = useState('');
  const [note, setNote] = useState('');
  const [error, setError] = useState('');
  const searchBox = useRef<HTMLInputElement>(null);

  /** Shows `word`; `record` adds it to the Back and Forward trail. */
  async function lookUp(word: string, record: boolean, on = target) {
    const text = word.trim();
    if (text === '') return;
    if (record) {
      setHistory((h) => {
        const words = h.words.slice(0, h.at + 1);
        if (words.length === 0 || words.at(-1)!.toLowerCase() !== text.toLowerCase()) words.push(text);
        return { words, at: words.length - 1 };
      });
    }
    setSearch(text);
    setError('');
    const forTarget = on !== null && on.word.toLowerCase() === text.toLowerCase();
    try {
      const found = await thesaurus.lookUp(text, forTarget ? on.before : null);
      if (forTarget && targetReading.current === null && found.senses.length > 0) targetReading.current = found.senses[0]!.reading;
      setLookup(found);
      setIsTarget(forTarget);
      setPicked(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : "The thesaurus couldn't be loaded.");
    }
  }

  // Opened on a word (or to browse): start again from it.
  useEffect(() => {
    setTarget(opened);
    targetReading.current = null;
    setHistory({ words: [], at: -1 });
    setReplacement('');
    setNote('');
    setLookup(null);
    if (opened && opened.word.trim() !== '') void lookUp(opened.word, true, opened);
    else {
      setSearch('');
      searchBox.current?.focus();
      void thesaurus.fit([], null, ''); // starts loading, so the first lookup is quick
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openedCount]);

  function go(step: number) {
    const at = history.at + step;
    if (at < 0 || at >= history.words.length) return;
    setHistory({ ...history, at });
    void lookUp(history.words[at]!, false);
  }

  /** While browsing away from the target word, a pick takes its form if it's the same part of speech. */
  function formFor(sense: Sense): Reading {
    if (isTarget) return sense.reading;
    const t = targetReading.current;
    return { lemma: sense.reading.lemma, partOfSpeech: sense.meaning.partOfSpeech, inflection: t && t.partOfSpeech === sense.meaning.partOfSpeech ? t.inflection : 'BASE' };
  }

  async function choose(word: string, sense: Sense, key: string, twice: boolean) {
    if (twice || !target || target.word.trim() === '') {
      void lookUp(word, true);
      return;
    }
    const [fitted] = await thesaurus.fit([word], formFor(sense), target.word);
    setPicked({ key, word: fitted! });
    setReplacement(fitted!);
    setNote('');
  }

  function replace(e?: FormEvent) {
    e?.preventDefault();
    if (!target || replacement.trim() === '') return;
    if (target.replace(replacement)) {
      setNote(`Replaced “${target.word}” with “${replacement}”.`);
      setTarget(null);
    } else {
      const keys = keysOf('writing.thesaurus').map(keyLabel).join(' or ');
      setNote(`“${target.word}” has changed in the text, or its editor has closed, so it wasn't replaced. Right-click the word again${keys ? `, or press ${keys} on it` : ''}.`);
    }
  }

  const replacing = target !== null && target.word.trim() !== '';
  let lastReading: Reading | null = null;
  let number = 0;

  return (
    <aside
      className="side-panel thesaurus-panel"
      aria-label="Thesaurus"
      onKeyDown={(e) => {
        if (matches(e, 'thesaurus.back')) {
          e.preventDefault();
          go(-1);
        } else if (matches(e, 'thesaurus.forward')) {
          e.preventDefault();
          go(1);
        } else if (e.key === 'Escape') {
          closeThesaurus();
        }
      }}
    >
      <div className="side-panel-head">
        <h2>Thesaurus</h2>
        <button type="button" className="quiet" aria-label="Close the Thesaurus" onClick={closeThesaurus}>
          ✕
        </button>
      </div>
      <form
        className="thesaurus-search"
        onSubmit={(e) => {
          e.preventDefault();
          void lookUp(search, true);
        }}
      >
        <button type="button" disabled={history.at <= 0} title={`Back (${keysOf('thesaurus.back').map(keyLabel).join(' or ')})`} aria-label="Back" onClick={() => go(-1)}>
          ◀
        </button>
        <button type="button" disabled={history.at < 0 || history.at >= history.words.length - 1} title={`Forward (${keysOf('thesaurus.forward').map(keyLabel).join(' or ')})`} aria-label="Forward" onClick={() => go(1)}>
          ▶
        </button>
        <input ref={searchBox} type="search" value={search} placeholder="Look up a word" aria-label="Look up a word" title="Type a word and press Enter to see its meanings and synonyms" onChange={(e) => setSearch(e.currentTarget.value)} />
        <button type="submit" title="Look up the word in the box (Enter)">
          Look Up
        </button>
      </form>
      <div className="thesaurus-results" aria-live="polite">
        {error ? (
          <p className="notice-error">{error}</p>
        ) : status === 'loading' && !lookup ? (
          <p className="meta">Loading the thesaurus… (the first time, about 6 MB)</p>
        ) : !lookup ? (
          <p className="meta">Type a word and press Enter.</p>
        ) : lookup.senses.length === 0 ? (
          <p className="meta">No entries for “{lookup.written}”. Names and invented words have none - try its dictionary form, or a plainer word.</p>
        ) : (
          lookup.senses.map((sense, i) => {
            const heading = !sameReading(lastReading, sense.reading);
            if (heading) {
              lastReading = sense.reading;
              number = 0;
            }
            number++;
            const form = FORM_NAMES[sense.reading.inflection];
            return (
              <div key={sense.meaning.id}>
                {heading && (
                  <h3 className="thesaurus-heading">
                    {sense.reading.lemma} · {POS_LABELS[sense.meaning.partOfSpeech]}
                    {form && sense.reading.lemma.toLowerCase() !== lookup.written.toLowerCase() ? ` (“${lookup.written}” is its ${form})` : ''}
                  </h3>
                )}
                <div className="thesaurus-sense">
                  <p className="thesaurus-definition">
                    {number}. {sense.meaning.definition}
                  </p>
                  {sense.meaning.example.trim() !== '' && <p className="thesaurus-example">“{sense.meaning.example}”</p>}
                  {(
                    [
                      ['Synonyms', sense.synonyms],
                      ['Similar', sense.similar],
                      ['More specific', sense.narrower],
                      ['More general', sense.broader],
                      ['Opposites', sense.opposites],
                    ] as const
                  ).map(([caption, words]) =>
                    words.length === 0 ? null : (
                      <div className="thesaurus-row" key={caption}>
                        <span className="thesaurus-caption">{caption}</span>
                        <span className="thesaurus-words">
                          {words.map((word) => {
                            const key = `${i}:${caption}:${word}`;
                            return (
                              <button
                                key={key}
                                type="button"
                                className={picked?.key === key ? 'thesaurus-word picked' : 'thesaurus-word'}
                                aria-pressed={picked?.key === key}
                                title={replacing ? 'Click to pick it, double-click to look it up' : 'Click to look it up'}
                                onClick={(e) => void choose(word, sense, key, e.detail >= 2)}
                              >
                                {word}
                              </button>
                            );
                          })}
                        </span>
                      </div>
                    ),
                  )}
                </div>
              </div>
            );
          })
        )}
      </div>
      {replacing && (
        <form className="thesaurus-replace" onSubmit={replace} aria-label="Replace">
          <label>
            Replace “{target.word}” with:
            <span className="thesaurus-replace-row">
              <input value={replacement} placeholder="Pick a word above" title="The word to put in - pick one above, or type your own" onChange={(e) => setReplacement(e.currentTarget.value)} />
              <button type="submit" className="primary" disabled={replacement.trim() === ''} title="Put this word in place of the one in your text (Enter)">
                Replace
              </button>
            </span>
          </label>
        </form>
      )}
      {note && (
        <p className="meta" role="status">
          {note}
        </p>
      )}
      <p className="meta thesaurus-credit">Open English WordNet 2025 (CC BY 4.0); word forms from LanguageTool (LGPL).</p>
    </aside>
  );
}
